import type { TFunction } from "i18next";
import type { TimelineBlock } from "../api/types";

type Labelled = Pick<TimelineBlock, "title" | "djs" | "artist" | "city" | "marker">;

/**
 * What a block is called, in the tiers that draw it.
 *
 * A block says at most three things -- what it is, who played it, where it came from -- and
 * they are a hierarchy, not a sentence. Split here rather than in the renderer because the
 * rules are about the fields and not about pixels: the pieces are edited separately in the
 * inspector and matched separately against the schedule, and they are rejoined by
 * `blockLabel` for the one place with a single row of text to spend.
 */
export interface BlockLines {
  /**
   * The heading: the title, or the performer when there is no title. A caption whose only
   * line is the dim one reads as a block that failed to name itself.
   */
  name: string | null;
  /**
   * Who played it -- a show's DJs, a track's artist. Both answer "who made this".
   *
   * Null when it would repeat the heading. The matcher falls back to the performer when a
   * scheduled show carries no title of its own, so these two are routinely the same string,
   * and printing it twice reads as a bug.
   */
  performer: string | null;
  /**
   * Where it came from: "LIVE from Manchester".
   *
   * The marker is quoted rather than assumed. "RECORDED at" is a different claim from "LIVE
   * from", and printing every block as live would put words in the station's mouth.
   *
   * The tooltip's, not the canvas's: it is the same clause on every block of a night, so a
   * lane of them repeats one phrase down the row and separates nothing.
   */
  origin: string | null;
}

export function blockLines(block: Labelled, t: TFunction): BlockLines {
  const title = block.title?.trim() || null;
  const performer = block.djs?.trim() || block.artist?.trim() || null;
  // Nothing identifying at all, so nothing to hang the origin off either: ", LIVE from
  // Manchester" is not a caption. The renderer draws its own placeholder instead.
  if (!title && !performer) return { name: null, performer: null, origin: null };
  const city = block.city?.trim() || null;
  return {
    name: title ?? performer,
    performer: title && performer && !same(performer, title) ? performer : null,
    origin: city ? `${block.marker?.trim() || t("label.liveFrom")} ${city}` : null,
  };
}

/**
 * The same tiers joined into the line the station announces a show with -- "Djinn - Formless,
 * LIVE from Manchester" -- for the tooltip, which is one row of text.
 */
export function blockLabel(block: Labelled, t: TFunction): string | null {
  const { name, performer, origin } = blockLines(block, t);
  if (!name) return null;
  const head = performer ? `${performer} - ${name}` : name;
  return origin ? `${head}, ${origin}` : head;
}

function same(left: string, right: string): boolean {
  return left.localeCompare(right, undefined, { sensitivity: "accent" }) === 0;
}
